<template>
  <div>
    <section>
      <div class="container mt-3">
        <!-- Header -->
        <div class="row justify-content-center">
          <div class="col-12 text-center">
            <h2>Select A Vendor</h2>
          </div>

          <!-- "Can't Find Vendor" Button -->
          <div class="col-12 text-center mt-3">
            <button class="btn btn-primary" @click="findCustomVendor">
              Can't Find Vendor
            </button>
          </div>

          <div v-show="!vendorNotFound" class="col-12">
            <!-- Search Input -->
            <div class="d-flex justify-content-center mt-3">
              <input
                v-model="searchQuery"
                class="form-control w-75 w-sm-100"
                placeholder="Search vendors..."
              />
            </div>

            <!-- "Search Near Me" Button -->
            <div class="text-center mt-3">
              <button @click="searchNearMe" class="btn btn-primary">
                Search Near Me
              </button>
            </div>

            <!-- Loading Spinner -->
            <div v-if="isLoading" class="text-center mt-3">
              <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
              </div>
              <p>Please wait, calculating distances...</p>
            </div>

            <!-- Vendors Table -->
            <div class="table-responsive mt-3 table-container">
              <table
                v-if="!isLoading && paginatedVendors.length"
                class="table table-bordered mx-auto"
              >
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Address</th>
                    <th v-if="userLocation">Distance (km)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="vendor in paginatedVendors"
                    :key="vendor._id"
                  >
                    <td>{{ vendor.employment }}</td>
                    <td>{{ vendor.address }}</td>
                    <td v-if="userLocation">
                      {{ vendor.distance ? vendor.distance.toFixed(2) + ' km' : 'N/A' }}
                    </td>
                    <td>
                      <button
                        @click="selectVendor(vendor)"
                        class="btn btn-primary"
                      >
                        Select
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-if="!isLoading && !paginatedVendors.length" class="text-center">
                No vendors found.
              </p>
            </div>

            <!-- Pagination Controls (max 5 page numbers + Next) -->
            <nav
              class="d-flex justify-content-center my-3"
              v-if="totalPages > 1"
            >
              <ul class="pagination mb-0">
                <li
                  class="page-item"
                  :class="{ disabled: currentPage === 1 }"
                  @click="goTo(currentPage - 1)"
                >
                  <a class="page-link">Prev</a>
                </li>
                <li
                  v-for="p in displayPages"
                  :key="p"
                  class="page-item"
                  :class="{ active: p === currentPage }"
                  @click="goTo(p)"
                >
                  <a class="page-link">{{ p }}</a>
                </li>
                <li
                  class="page-item"
                  :class="{ disabled: currentPage === totalPages }"
                  @click="goTo(currentPage + 1)"
                >
                  <a class="page-link">Next</a>
                </li>
              </ul>
            </nav>
          </div>

          <div v-show="vendorNotFound" class="col-12 text-center mt-3">
            <input
              v-model="name"
              class="form-control w-75 w-sm-100 mb-2"
              placeholder="Vendor name..."
            />
            <input
              v-model="address"
              class="form-control w-75 w-sm-100"
              placeholder="Address..."
            />
            <div class="mt-3">
              <button
                class="btn btn-primary"
                @click="selectCustomVendor"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'VendorSearchup',
  data() {
    return {
      name: '',
      address: '',
      vendorNotFound: false,
      searchQuery: '',
      vendors: [],
      userLocation: null,
      isLoading: false,
      currentPage: 1,
      pageSize: 8,
    }
  },
  computed: {
    filteredVendors() {
      let list = this.vendors
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase().split(' ')
        list = list.filter(vendor => {
          const combined = `${vendor.employment} ${vendor.address}`.toLowerCase()
          return q.every(word => combined.includes(word))
        })
      }
      return list
    },
    paginatedVendors() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredVendors.slice(start, start + this.pageSize)
    },
    totalPages() {
      return Math.ceil(this.filteredVendors.length / this.pageSize) || 1
    },
    displayPages() {
      const max = 5
      let start = Math.max(1, this.currentPage - Math.floor(max / 2))
      let end = start + max - 1
      if (end > this.totalPages) {
        end = this.totalPages
        start = Math.max(1, end - max + 1)
      }
      const pages = []
      for (let i = start; i <= end; i++) pages.push(i)
      return pages
    }
  },
  created() {
    this.fetchVendors()
  },
  methods: {
    async fetchVendors() {
      try {
        const vendors = await this.$store.dispatch('fetchVendors')
        this.vendors = vendors
      } catch (e) {
        console.error(e)
      }
    },
    selectVendor(vendor) {
      this.$store.commit('SET_SELECTED_VENDOR', vendor._id)
      localStorage.setItem('vendor', vendor._id)
      this.$router.push({ name: 'UserCreate' })
    },
    findCustomVendor() {
      this.vendorNotFound = !this.vendorNotFound
    },
    selectCustomVendor() {
      const fallbackId = '5eb8e595a48efa0ba9073519'
      this.$store.commit('SET_SELECTED_VENDOR', fallbackId)
      localStorage.setItem('vendor', fallbackId)
      this.$router.push({ name: 'UserCreate' })
    },
    async searchNearMe() {
      if (!navigator.geolocation) return alert('Geolocation not supported')
      this.isLoading = true
      navigator.geolocation.getCurrentPosition(async pos => {
        this.userLocation = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude
        }
        await this.calculateDistances()
        this.isLoading = false
      }, err => {
        console.error(err)
        alert('Unable to access location')
        this.isLoading = false
      })
    },
    async calculateDistances() {
      // ... existing distance logic ...
    },
    haversine(lat1, lon1, lat2, lon2) {
      const R = 6371
      const toRad = v => (v * Math.PI) / 180
      const dLat = toRad(lat2 - lat1)
      const dLon = toRad(lon2 - lon1)
      const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1))*Math.cos(toRad(lat2))*Math.sin(dLon/2)**2
      return 2*R*Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
    },
    goTo(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
      }
    }
  }
}
</script>

<style scoped>
/* Limit width to 70% on desktop, full width on mobile */
.table-container {
  max-width: 70%;
  margin: auto;
}
@media (max-width: 768px) {
  .table-container {
    max-width: 100%;
  }
}
h2 {
  font-size: 1.5rem;
  line-height: 1.4;
  color: #009579;
}
.search {
  padding: 12px 0;
  text-align: center;
  background-color: #eee;
  width: 100%;
}
.search input {
  width: 100%;
  padding: 8px;
  margin-bottom: 10px;
  max-width: 400px;
}
.table {
  width: 100%;
  margin-top: 20px;
}
.table th,
.table td {
  text-align: left;
  vertical-align: middle;
}
.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}
.address-input {
  max-width: 400px;
  width: 100%;
  text-align: center;
}
.spinner-border {
  width: 3rem;
  height: 3rem;
}
@media (max-width: 576px) {
  h2 {
    font-size: 1.25rem;
  }
  .table td {
    display: block;
    text-align: left;
  }
  .table thead {
    display: none;
  }
  .table td button {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/vendorSearchup.vue